import React, { useCallback, useState } from "react";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MobileScreenSettingsServiceRelation } from "@/types/mobile-settings";
import { HotelService } from "@/types/hotelService";
import { SortableList } from "@/components/generic/SortableList";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AddServiceDialog } from "./AddServiceDialog";

const MAX_CHAT_SERVICES = 6;

interface ChatDisplaySettingsProps {
  hotelId: string;
  isEnabled: boolean;
  items: MobileScreenSettingsServiceRelation[];
  onEnabledChange: (isEnabled: boolean) => void;
  onItemsChange: (items: MobileScreenSettingsServiceRelation[]) => void;
}

interface ChatServiceRowProps {
  item: MobileScreenSettingsServiceRelation;
  onDelete: (item: MobileScreenSettingsServiceRelation) => void;
}

const normalizeOrder = (
  items: MobileScreenSettingsServiceRelation[],
): MobileScreenSettingsServiceRelation[] =>
  items.map((item, order) => ({ ...item, order }));

const ChatServiceRow: React.FC<ChatServiceRowProps> = ({ item, onDelete }) => {
  const { t } = useTranslation();
  const handleDelete = () => onDelete(item);

  return (
    <div className="bg-card flex w-full items-center gap-3 rounded-xl border px-4 py-3 shadow-sm">
      <GripVertical className="text-muted-foreground size-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{item.service.name}</p>
        <p className="text-muted-foreground truncate text-sm">
          {item.service.category.name}
        </p>
      </div>
      <Button
        type="button"
        variant="new_ghost"
        size="icon_sm"
        onClick={handleDelete}
        aria-label={t("mobileSettings.customization.chatDisplay.removeService")}
      >
        <Trash2 />
      </Button>
    </div>
  );
};

export const ChatDisplaySettings: React.FC<ChatDisplaySettingsProps> = ({
  hotelId,
  isEnabled,
  items,
  onEnabledChange,
  onItemsChange,
}) => {
  const { t } = useTranslation();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const hasReachedLimit = items.length >= MAX_CHAT_SERVICES;

  const handleReorder = useCallback(
    (reorderedItems: MobileScreenSettingsServiceRelation[]) => {
      onItemsChange(normalizeOrder(reorderedItems));
    },
    [onItemsChange],
  );

  const handleDelete = useCallback(
    (item: MobileScreenSettingsServiceRelation) => {
      onItemsChange(
        normalizeOrder(items.filter((candidate) => candidate.id !== item.id)),
      );
    },
    [items, onItemsChange],
  );

  const handleAdd = useCallback(
    (service: HotelService) => {
      if (hasReachedLimit) return;

      onItemsChange([
        ...items,
        {
          id: `chat-${service.id}`,
          serviceId: service.id,
          order: items.length,
          service,
        },
      ]);
    },
    [hasReachedLimit, items, onItemsChange],
  );

  const handleOpenAddDialog = () => setIsAddDialogOpen(true);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-xl font-bold">
            {t("mobileSettings.customization.chatDisplay.title")}
          </h3>
          <p className="text-muted-foreground text-sm">
            {t("mobileSettings.customization.chatDisplay.description")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="chat-display-enabled"
            checked={isEnabled}
            onCheckedChange={onEnabledChange}
          />
          <Label htmlFor="chat-display-enabled">
            {t("mobileSettings.customization.chatDisplay.enabled")}
          </Label>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <p className="font-medium">
          {t("mobileSettings.customization.chatDisplay.listCount", {
            count: items.length,
            max: MAX_CHAT_SERVICES,
          })}
        </p>
        <Button
          type="button"
          variant="new_outline"
          size="sm"
          onClick={handleOpenAddDialog}
          disabled={hasReachedLimit}
        >
          <Plus />
          {t("mobileSettings.customization.chatDisplay.addService")}
        </Button>
      </div>

      {items.length > 0 ? (
        <SortableList
          items={items}
          onReorder={handleReorder}
          keyExtractor={(item) => item.id}
          direction="column"
          className="flex flex-col gap-3"
          renderItem={(item) => (
            <ChatServiceRow item={item} onDelete={handleDelete} />
          )}
        />
      ) : (
        <div className="text-muted-foreground rounded-xl border border-dashed p-6 text-center text-sm">
          {t("mobileSettings.customization.chatDisplay.empty")}
        </div>
      )}

      <AddServiceDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onAdd={handleAdd}
        hotelId={hotelId}
        existingIds={items.map((item) => item.serviceId)}
        title={t("mobileSettings.customization.chatDisplay.addService")}
        type="chat"
      />
    </section>
  );
};
